<template>
  <b-modal
    v-model="store.showModal"
    size="sm"
    modal-class="speech-input-modal"
    centered
    no-close-on-backdrop
    no-close-on-esc
    hide-header-close
    hide-header
    body-class="speech-modal-body text-center py-1 px-1"
    footer-class="speech-modal-footer d-flex justify-content-center border-0 pt-0 pb-1"
    @shown="onShown"
    @hidden="onHidden"
  >
    <!-- Mic icon with pulse -->
    <div class="speech-mic-visual my-75">
      <div
        class="mic-circle"
        :class="{ 'is-listening': store.isListening, 'is-error': store.status === 'error' }"
      >
        <feather-icon
          icon="MicIcon"
          size="28"
        />
      </div>
    </div>

    <!-- Audio wave animation -->
    <div
      v-if="store.isListening"
      class="speech-wave mb-50"
    >
      <span /><span /><span /><span /><span />
    </div>

    <!-- Status text -->
    <p
      v-if="store.status === 'listening'"
      class="small text-muted mb-25"
    >
      Mendengarkan...
    </p>
    <p
      v-else-if="store.status === 'stopping'"
      class="small text-muted mb-25"
    >
      Memproses...
    </p>
    <p
      v-else-if="store.status === 'error'"
      class="small text-danger mb-25"
    >
      {{ store.errorMessage }}
    </p>
    <p
      v-else-if="store.status === 'requesting_permission'"
      class="small text-muted mb-25"
    >
      Meminta izin mikrofon...
    </p>
    <p
      v-else
      class="small text-muted mb-25"
    >
      Memulai...
    </p>

    <!-- Transcript preview -->
    <div
      v-if="store.displayTranscript"
      class="speech-transcript mb-50 px-50"
    >
      <p class="small mb-0 text-left">
        {{ store.displayTranscript }}
      </p>
    </div>

    <!-- Footer buttons -->
    <template #modal-footer>
      <b-button
        variant="outline-secondary"
        size="sm"
        class="px-1"
        @click="handleCancel"
      >
        Batal
      </b-button>
      <b-button
        v-if="store.isListening"
        variant="primary"
        size="sm"
        class="px-1"
        @click="handleStop"
      >
        <feather-icon
          icon="CheckIcon"
          size="14"
          class="mr-25"
        />
        Selesai
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { defineComponent } from '@vue/composition-api'
import { useSpeechInputStore } from '@/pinia/speechInput'

export default defineComponent({
  name: 'SpeechInputModal',
  setup() {
    const store = useSpeechInputStore()

    function onShown() {
      store.startListening()
    }

    function onHidden() {
      if (store.isListening || store.status === 'listening') {
        store.cancel()
      }
    }

    function handleStop() {
      store.stopListening()
    }

    function handleCancel() {
      store.cancel()
    }

    return { store, onShown, onHidden, handleStop, handleCancel }
  },
})
</script>

<style lang="scss">
.speech-input-modal {
  .modal-dialog {
    max-width: 320px;
  }

  .speech-mic-visual {
    display: flex;
    justify-content: center;

    .mic-circle {
      width: 64px;
      height: 64px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f0f0f0;
      color: #636363;
      transition: all 0.3s ease;

      &.is-listening {
        background: #e8f5e9;
        color: #28c76f;
        animation: mic-pulse 1.5s ease-in-out infinite;
      }

      &.is-error {
        background: #fce4ec;
        color: #ea5455;
      }
    }
  }

  .speech-wave {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    height: 24px;

    span {
      display: inline-block;
      width: 3px;
      background: #28c76f;
      border-radius: 2px;
      animation: speech-wave-bar 1s ease-in-out infinite;

      &:nth-child(1) { animation-delay: 0s; }
      &:nth-child(2) { animation-delay: 0.1s; }
      &:nth-child(3) { animation-delay: 0.2s; }
      &:nth-child(4) { animation-delay: 0.3s; }
      &:nth-child(5) { animation-delay: 0.4s; }
    }
  }

  .speech-transcript {
    max-height: 80px;
    overflow-y: auto;
    background: #f8f8f8;
    border-radius: 4px;
    padding: 6px 8px;
  }
}

@keyframes mic-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(40, 199, 111, 0.3); }
  50% { box-shadow: 0 0 0 12px rgba(40, 199, 111, 0); }
}

@keyframes speech-wave-bar {
  0%, 100% { height: 4px; }
  50% { height: 20px; }
}
</style>
